import React, {useEffect, useId, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ActivationView} from '../../domain/entities/Activation.ts'
import {ActivationAction, availableActivationActions} from './activationView.ts'

const LABEL_KEY: Record<ActivationAction, string> = {
    editContact: 'activation.action.editContact',
    markNoContact: 'activation.action.markNoContact',
    confirmConsent: 'activation.action.confirmConsent',
    requestExtra: 'activation.action.requestExtra',
    cancel: 'activation.action.cancel',
}

interface ActivationActionsMenuProps {
    activation: ActivationView
    disabled?: boolean
    onAction: (action: ActivationAction, activation: ActivationView) => void
}

// Compact "···" menu on the row. Keyboard: Enter/Space opens, arrows move,
// Escape closes and returns focus to the trigger.
export const ActivationActionsMenu: React.FC<ActivationActionsMenuProps> = ({activation, disabled, onAction}) => {
    const {t} = useTranslation('dashboard')
    const [isOpen, setIsOpen] = useState(false)
    const rootRef = useRef<HTMLSpanElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
    const menuId = useId()
    const actions = availableActivationActions(activation)
    // The label adds the contact-failed fix when that's what the row needs.
    const editLabel = activation.flags?.contactFailed ? 'activation.action.fixContact' : LABEL_KEY.editContact

    useEffect(() => {
        if (!isOpen) return
        itemRefs.current[0]?.focus()
        const onDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setIsOpen(false)
        }
        document.addEventListener('mousedown', onDown)
        return () => document.removeEventListener('mousedown', onDown)
    }, [isOpen])

    if (actions.length === 0) return null

    const close = () => {
        setIsOpen(false)
        triggerRef.current?.focus()
    }

    const onMenuKeyDown = (e: React.KeyboardEvent) => {
        const items = itemRefs.current.filter(Boolean) as HTMLButtonElement[]
        const index = items.indexOf(document.activeElement as HTMLButtonElement)
        if (e.key === 'Escape') {e.preventDefault(); close()}
        else if (e.key === 'ArrowDown') {e.preventDefault(); items[(index + 1) % items.length]?.focus()}
        else if (e.key === 'ArrowUp') {e.preventDefault(); items[(index - 1 + items.length) % items.length]?.focus()}
        else if (e.key === 'Tab') setIsOpen(false)
    }

    return (
        <span ref={rootRef} className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
            <button ref={triggerRef} type="button" disabled={disabled}
                    aria-haspopup="menu" aria-expanded={isOpen} aria-controls={isOpen ? menuId : undefined}
                    aria-label={t('activation.action.menu', {code: activation.code})}
                    onClick={() => setIsOpen((o) => !o)}
                    className="h-6 px-2 inline-flex items-center rounded-full border border-neutralBlack-300 bg-white text-[11.5px] font-medium text-neutralBlack-900 hover:bg-neutralBlack-100 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                {t('activation.action.menuShort')}
            </button>
            {isOpen && (
                <span id={menuId} role="menu" onKeyDown={onMenuKeyDown}
                      className="absolute z-20 top-7 left-0 min-w-[220px] flex flex-col p-1.5 bg-white border border-neutralBlack-200 rounded-xl shadow-[0_12px_28px_rgba(26,26,26,.13)]">
                    {actions.map((action, i) => (
                        <button key={action} ref={(el) => {itemRefs.current[i] = el}} type="button" role="menuitem"
                                onClick={() => {setIsOpen(false); onAction(action, activation)}}
                                className={`text-left px-3 py-2 rounded-lg text-[13px] font-medium whitespace-nowrap focus:outline-none focus-visible:bg-neutralBlack-100 hover:bg-neutralBlack-100 ${action === 'cancel' ? 'text-[#B3261E]' : 'text-neutralBlack-900'}`}>
                            {t(action === 'editContact' ? editLabel : LABEL_KEY[action])}
                        </button>
                    ))}
                </span>
            )}
        </span>
    )
}
